import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Megaphone, Save, AlertCircle, CheckCircle2, Info, TrendingDown, Calendar, Sparkles, TrendingUp, Rocket } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, ComposedChart, Line, Legend, Cell } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

const fmtEur = (v) => {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};
const fmtPct = (v) => v == null || isNaN(v) ? "—" : `${v.toFixed(1)}%`;

const STATUS_STYLE = {
  green: { bg: "bg-emerald-50", border: "border-emerald-300", text: "text-emerald-800", emoji: "🟢", icon: CheckCircle2 },
  yellow: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", emoji: "🟡", icon: AlertCircle },
  red: { bg: "bg-rose-50", border: "border-rose-300", text: "text-rose-800", emoji: "🔴", icon: TrendingDown },
  info: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-800", emoji: "🔵", icon: Info },
  neutral: { bg: "bg-slate-50", border: "border-slate-300", text: "text-slate-600", emoji: "⚪", icon: Info },
};

export const MarketingROIPage = ({ clientId, year }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expenses, setExpenses] = useState({});
  const [categories, setCategories] = useState([]);
  const [categoryLabels, setCategoryLabels] = useState({});
  const [roi, setRoi] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, roiRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/marketing-expenses?year=${year}`),
        axios.get(`${API}/clients/${clientId}/marketing-roi?year=${year}`),
      ]);
      setCategories(expRes.data.categories || []);
      setCategoryLabels(expRes.data.category_labels || {});
      const grid = {};
      for (let m = 1; m <= 12; m++) grid[m] = {};
      (expRes.data.rows || []).forEach((r) => {
        grid[r.month][r.category] = r.amount;
      });
      setExpenses(grid);
      setRoi(roiRes.data);
    } catch (e) {
      toast.error("Errore caricamento marketing ROI");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [clientId, year]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleChange = (month, category, value) => {
    const v = value === "" ? 0 : parseFloat(value.replace(",", ".")) || 0;
    setExpenses((prev) => ({ ...prev, [month]: { ...prev[month], [category]: v } }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const rows = [];
      for (let m = 1; m <= 12; m++) {
        for (const cat of categories) {
          const amt = expenses[m]?.[cat];
          if (amt !== undefined) rows.push({ year, month: m, category: cat, amount: amt });
        }
      }
      await axios.put(`${API}/clients/${clientId}/marketing-expenses`, { rows });
      toast.success("Spese marketing salvate");
      await fetchAll();
    } catch (e) {
      toast.error("Errore salvataggio");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Caricamento Marketing ROI…</div>;

  const monthsLoaded = roi?.months_loaded || 0;
  const periodLabel = monthsLoaded > 0 ? `Gen–${MONTHS_IT[monthsLoaded - 1]} ${year}` : `${year}`;
  const mdcPct = roi?.mdc_pct ?? 0;
  const mdcFactor = mdcPct > 0 ? mdcPct / 100 : 0;
  // Per €100 spend, revenue extra needed at ROAS=1 (only repay): 100/mdc_factor
  const minRevPer100 = mdcFactor > 0 ? 100 / mdcFactor : 0;

  // Helper: compute extra revenue needed for given spend at given ROAS multiplier
  const targetRev = (spend, roasX) => {
    if (!spend || mdcFactor <= 0) return 0;
    return (spend * roasX) / mdcFactor;
  };

  // Build monthly target table (for ALL 12 months based on what's in expenses state)
  const monthlyTargets = Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
    const spendTot = categories.reduce((s, c) => s + (expenses[m]?.[c] || 0), 0);
    return {
      month: m,
      label: MONTHS_IT[m - 1],
      isLoaded: m <= monthsLoaded,
      spend: spendTot,
      target_1x: targetRev(spendTot, 1),
      target_2x: targetRev(spendTot, 2),
      target_3x: targetRev(spendTot, 3),
    };
  });

  const totalSpendYTD = roi?.total_marketing_ytd || 0;
  const totalSpendAnnual = roi?.total_marketing_annual_input || 0;
  const totalTarget1x_ytd = targetRev(totalSpendYTD, 1);
  const totalTarget2x_ytd = targetRev(totalSpendYTD, 2);
  const totalTarget3x_ytd = targetRev(totalSpendYTD, 3);
  const totalTarget1x_annual = targetRev(totalSpendAnnual, 1);
  const totalTarget2x_annual = targetRev(totalSpendAnnual, 2);
  const totalTarget3x_annual = targetRev(totalSpendAnnual, 3);

  return (
    <div className="space-y-6" data-testid="marketing-roi-page">
      {/* HEADER NARRATIVO */}
      <Card className="border-2 border-violet-300 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-white">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-fuchsia-600" />
            Marketing ROI — Quanto Fatturato Extra deve Portare ogni Euro Speso
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-white/70 rounded-xl p-4 border border-violet-200">
            <p className="text-sm leading-relaxed text-slate-800">
              <strong className="text-violet-800">Il marketing non è un costo ma un investimento.</strong> Per ogni euro speso, deve generare un certo fatturato extra per ripagarsi.
              Il moltiplicatore necessario dipende dal tuo margine di contribuzione: per coprire 1€ di marketing servono <strong>{(1/mdcFactor || 0).toFixed(2)}€ di fatturato extra</strong> (a MdC={fmtPct(mdcPct)}).
            </p>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
              <div className="flex items-start gap-2 p-2 bg-amber-50 rounded border border-amber-200">
                <CheckCircle2 className="h-4 w-4 text-amber-700 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-amber-900">ROAS 1× — Si ripaga</div>
                  <div className="text-[11px] text-amber-800">Solo copre la spesa, profitto nullo</div>
                </div>
              </div>
              <div className="flex items-start gap-2 p-2 bg-emerald-50 rounded border border-emerald-200">
                <TrendingUp className="h-4 w-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-900">ROAS 2× — Investimento</div>
                  <div className="text-[11px] text-emerald-800">Spesa ripagata + utile pari</div>
                </div>
              </div>
              <div className="flex items-start gap-2 p-2 bg-violet-100 rounded border border-violet-300">
                <Rocket className="h-4 w-4 text-violet-700 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-violet-900">ROAS 3× — Opportunità</div>
                  <div className="text-[11px] text-violet-800">Investimento ad alto rendimento</div>
                </div>
              </div>
            </div>
          </div>

          {/* KPI quick view */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="border rounded-lg p-3 bg-fuchsia-50 border-fuchsia-200">
              <div className="text-xs text-fuchsia-700 uppercase tracking-wide font-semibold">Spesa Marketing — Periodo</div>
              <div className="text-2xl font-bold text-fuchsia-900 tabular-nums">{fmtEur(roi?.total_marketing_ytd)}</div>
              <div className="text-[11px] text-fuchsia-600 mt-1">{periodLabel}</div>
            </div>
            <div className="border rounded-lg p-3 bg-violet-50 border-violet-200">
              <div className="text-xs text-violet-700 uppercase tracking-wide font-semibold">Spesa Marketing — Annua</div>
              <div className="text-2xl font-bold text-violet-900 tabular-nums">{fmtEur(roi?.total_marketing_annual_input)}</div>
              <div className="text-[11px] text-violet-600 mt-1">Tutti i mesi in tabella</div>
            </div>
            <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
              <div className="text-xs text-emerald-700 uppercase tracking-wide font-semibold">Margine Contribuzione %</div>
              <div className="text-2xl font-bold text-emerald-900 tabular-nums">{fmtPct(roi?.mdc_pct)}</div>
              <div className="text-[11px] text-emerald-600 mt-1">Auto dal CE annuale</div>
            </div>
            <div className="border rounded-lg p-3 bg-blue-50 border-blue-200">
              <div className="text-xs text-blue-700 uppercase tracking-wide font-semibold">Per 100€ di Marketing</div>
              <div className="text-2xl font-bold text-blue-900 tabular-nums">{fmtEur(minRevPer100)}</div>
              <div className="text-[11px] text-blue-600 mt-1">fatturato extra minimo (ROAS 1×)</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* INPUT TABLE */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">📥 Inserimento Spese Mensili {year}</CardTitle>
            <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-marketing-expenses">
              <Save className="h-4 w-4" />
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <p className="text-xs text-muted-foreground mb-3 italic">
            💡 Puoi inserire spese anche per i mesi futuri (es. consulenze marketing già programmate). Saranno usate per la <strong>Proiezione Annua</strong> in fondo alla pagina.
          </p>
          <table className="w-full text-sm" data-testid="marketing-expense-table">
            <thead>
              <tr className="border-b bg-slate-50">
                <th className="text-left py-2 px-2 font-semibold sticky left-0 bg-slate-50 z-10 min-w-[120px]">Mese</th>
                {categories.map((c) => (
                  <th key={c} className="text-right py-2 px-2 font-semibold min-w-[120px]">
                    {categoryLabels[c]}
                    {c === "marketing_courses" && <span className="block text-[9px] text-blue-600 normal-case">investim. brand</span>}
                  </th>
                ))}
                <th className="text-right py-2 px-2 font-semibold bg-slate-100">Totale</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
                const rowTotal = categories.reduce((s, c) => s + (expenses[m]?.[c] || 0), 0);
                const isLoaded = m <= monthsLoaded;
                return (
                  <tr key={m} className={`border-b hover:bg-slate-50 ${isLoaded ? "" : "bg-violet-50/40"}`}>
                    <td className={`py-1.5 px-2 font-medium sticky left-0 z-10 ${isLoaded ? "bg-white" : "bg-violet-50"}`}>
                      {MONTHS_IT[m - 1]} {year}
                      {!isLoaded && <span className="ml-2 text-[9px] text-violet-700 uppercase">futuro</span>}
                    </td>
                    {categories.map((c) => (
                      <td key={c} className="py-1 px-1">
                        <Input
                          type="number"
                          step="0.01"
                          value={expenses[m]?.[c] ?? ""}
                          onChange={(e) => handleChange(m, c, e.target.value)}
                          className="text-right h-8 text-sm tabular-nums"
                          placeholder="0"
                          data-testid={`mkt-input-${m}-${c}`}
                        />
                      </td>
                    ))}
                    <td className="py-1.5 px-2 text-right tabular-nums font-semibold bg-slate-50">{rowTotal > 0 ? fmtEur(rowTotal) : "—"}</td>
                  </tr>
                );
              })}
              <tr className="border-t-2 bg-fuchsia-50 font-bold">
                <td className="py-2 px-2 sticky left-0 bg-fuchsia-50 z-10">TOTALE ANNO (input)</td>
                {categories.map((c) => {
                  const colTotal = Array.from({ length: 12 }, (_, i) => i + 1).reduce((s, m) => s + (expenses[m]?.[c] || 0), 0);
                  return <td key={c} className="py-2 px-2 text-right tabular-nums">{colTotal > 0 ? fmtEur(colTotal) : "—"}</td>;
                })}
                <td className="py-2 px-2 text-right tabular-nums bg-fuchsia-100">
                  {fmtEur(Array.from({ length: 12 }, (_, i) => i + 1).reduce(
                    (s, m) => s + categories.reduce((cs, c) => cs + (expenses[m]?.[c] || 0), 0), 0
                  ))}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="text-xs text-muted-foreground mt-3 italic">Le righe a 0 vengono cancellate al salvataggio. Le righe viola sono mesi futuri non ancora nel bilancio.</p>
        </CardContent>
      </Card>

      {/* TARGET MENSILE DI FATTURATO EXTRA PER MARKETING */}
      {totalSpendAnnual > 0 && mdcFactor > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Calendar className="h-4 w-4 text-violet-600" />
              🎯 Target Mensile di Fatturato Extra dal Marketing
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Per ogni mese, dato il budget marketing inserito, il fatturato extra che la campagna deve generare per ripagarsi (1×), per essere un investimento profittevole (2×) o un'opportunità (3×).
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="monthly-targets-table">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="text-left py-2 px-2 font-semibold">Mese</th>
                  <th className="text-right py-2 px-2 font-semibold text-fuchsia-700">Spesa Marketing</th>
                  <th className="text-right py-2 px-2 font-semibold text-amber-700">
                    Fatt. extra <span className="block text-[10px] font-normal">ROAS 1× (ripaga)</span>
                  </th>
                  <th className="text-right py-2 px-2 font-semibold text-emerald-700">
                    Fatt. extra <span className="block text-[10px] font-normal">ROAS 2× (investimento)</span>
                  </th>
                  <th className="text-right py-2 px-2 font-semibold text-violet-700">
                    Fatt. extra <span className="block text-[10px] font-normal">ROAS 3× (opportunità)</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {monthlyTargets.map((row) => (
                  <tr
                    key={row.month}
                    className={`border-b hover:bg-slate-50 ${row.spend > 0 ? "" : "opacity-50"} ${row.isLoaded ? "" : "bg-violet-50/40"}`}
                    data-testid={`target-row-${row.month}`}
                  >
                    <td className="py-1.5 px-2 font-medium">
                      {row.label} {year}
                      {!row.isLoaded && <span className="ml-2 text-[9px] text-violet-700 uppercase">futuro</span>}
                    </td>
                    <td className="text-right tabular-nums py-1.5 px-2">{row.spend > 0 ? fmtEur(row.spend) : "—"}</td>
                    <td className="text-right tabular-nums py-1.5 px-2 text-amber-800 font-semibold">{row.spend > 0 ? fmtEur(row.target_1x) : "—"}</td>
                    <td className="text-right tabular-nums py-1.5 px-2 text-emerald-800 font-semibold">{row.spend > 0 ? fmtEur(row.target_2x) : "—"}</td>
                    <td className="text-right tabular-nums py-1.5 px-2 text-violet-800 font-semibold">{row.spend > 0 ? fmtEur(row.target_3x) : "—"}</td>
                  </tr>
                ))}
                <tr className="border-t-2 bg-slate-100 font-bold">
                  <td className="py-2 px-2">TOTALE ANNO</td>
                  <td className="text-right tabular-nums py-2 px-2 text-fuchsia-900">{fmtEur(totalSpendAnnual)}</td>
                  <td className="text-right tabular-nums py-2 px-2 text-amber-900">{fmtEur(totalTarget1x_annual)}</td>
                  <td className="text-right tabular-nums py-2 px-2 text-emerald-900">{fmtEur(totalTarget2x_annual)}</td>
                  <td className="text-right tabular-nums py-2 px-2 text-violet-900">{fmtEur(totalTarget3x_annual)}</td>
                </tr>
              </tbody>
            </table>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="rounded-lg p-3 bg-amber-50 border border-amber-200">
                <div className="font-semibold text-amber-900 mb-1">📌 ROAS 1× — Sopravvivenza</div>
                <p className="text-amber-800 leading-relaxed">
                  Il fatturato extra <strong>copre solo la spesa</strong>. Profitto della campagna = 0.
                  È il <strong>minimo accettabile</strong>: sotto questa soglia il marketing è un costo secco.
                </p>
              </div>
              <div className="rounded-lg p-3 bg-emerald-50 border border-emerald-200">
                <div className="font-semibold text-emerald-900 mb-1">🎯 ROAS 2× — Obiettivo Sano</div>
                <p className="text-emerald-800 leading-relaxed">
                  Il fatturato extra ripaga la spesa <strong>e genera un utile pari</strong>.
                  È la <strong>soglia da puntare</strong> per ogni campagna marketing.
                </p>
              </div>
              <div className="rounded-lg p-3 bg-violet-50 border border-violet-200">
                <div className="font-semibold text-violet-900 mb-1">🚀 ROAS 3× — Eccellenza</div>
                <p className="text-violet-800 leading-relaxed">
                  Per ogni € speso ricevi <strong>3€ di fatturato</strong> = circa 2€ di utile aggiuntivo.
                  Risultato di campagne ben targettizzate o canali maturi.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* PER CATEGORIA: TARGET ROAS */}
      {roi?.categories_breakdown?.some((c) => c.expense > 0 || c.expense_annual_input > 0) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-emerald-600" />
              💼 Target di Ritorno per Categoria
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Per ogni voce di spesa marketing, il fatturato extra che ogni canale deve produrre.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {roi.categories_breakdown.filter((c) => c.expense > 0 || c.expense_annual_input > 0).map((c) => {
                const annualSpend = c.expense_annual_input || c.expense;
                const t1 = targetRev(annualSpend, 1);
                const t2 = targetRev(annualSpend, 2);
                const t3 = targetRev(annualSpend, 3);
                return (
                  <div key={c.category} className="border-2 rounded-lg p-4 bg-white border-slate-200" data-testid={`cat-card-${c.category}`}>
                    <div className="flex items-start justify-between mb-3 pb-2 border-b">
                      <div>
                        <div className="font-semibold text-sm text-slate-900">{c.label}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Spesa annua: <strong className="text-fuchsia-700 tabular-nums">{fmtEur(annualSpend)}</strong>
                          {c.expense > 0 && c.expense !== annualSpend && (
                            <span className="ml-2">(periodo: {fmtEur(c.expense)})</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between p-2 rounded bg-amber-50 border border-amber-200">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-3.5 w-3.5 text-amber-700" />
                          <span className="text-amber-900 font-medium">Per ripagarsi (1×)</span>
                        </div>
                        <span className="font-bold tabular-nums text-amber-900">{fmtEur(t1)}</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-emerald-50 border border-emerald-200">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-3.5 w-3.5 text-emerald-700" />
                          <span className="text-emerald-900 font-medium">Per essere investimento (2×)</span>
                        </div>
                        <span className="font-bold tabular-nums text-emerald-900">{fmtEur(t2)}</span>
                      </div>
                      <div className="flex items-center justify-between p-2 rounded bg-violet-50 border border-violet-200">
                        <div className="flex items-center gap-2">
                          <Rocket className="h-3.5 w-3.5 text-violet-700" />
                          <span className="text-violet-900 font-medium">Per essere opportunità (3×)</span>
                        </div>
                        <span className="font-bold tabular-nums text-violet-900">{fmtEur(t3)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* GRAFICO RIEPILOGATIVO ROAS */}
      {totalSpendAnnual > 0 && mdcFactor > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-600" />
              📊 Riepilogo Annuale: Spesa vs Fatturato Extra Necessario
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  { name: "Spesa marketing", value: totalSpendAnnual, fill: "#d946ef" },
                  { name: "ROAS 1× (ripaga)", value: totalTarget1x_annual, fill: "#f59e0b" },
                  { name: "ROAS 2× (investim.)", value: totalTarget2x_annual, fill: "#10b981" },
                  { name: "ROAS 3× (opportun.)", value: totalTarget3x_annual, fill: "#8b5cf6" },
                ]}
                margin={{ top: 10, right: 15, left: 0, bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" height={70} />
                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => fmtEur(v)} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-muted-foreground italic mt-3 text-center">
              Con {fmtEur(totalSpendAnnual)} di marketing all'anno, devi generare almeno {fmtEur(totalTarget1x_annual)} di fatturato extra per non perdere soldi.
              Punta a {fmtEur(totalTarget2x_annual)} per un'azienda sana, {fmtEur(totalTarget3x_annual)} per un'azienda in forte crescita.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default MarketingROIPage;
